import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { IChatConversation, IChatConversationUser } from '@fi-shared/fi-chat/fi-chat';
import { FiChatConversationType } from '@fi-shared/fi-chat/fi-chat-conversation-type';
import { FiProfileSizes } from '@fi-shared/profile-icon/profile-icon.component';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { FiChatHelperService } from '@fi-shared/fi-chat/fi-chat-helper.service';
import { FiChatSyncService } from '@fi-shared/fi-chat/fi-chat-sync.service';

@Component({
    selector: 'fi-chat-conversation-image',
    template: `
        <div class="conversation-image-container" [ngClass]="getConversationImageStyleClasses()">
          @for (conversationUser of conversationUsers; track conversationUser) {
            <fi-profile-icon
              [profileData]="conversationUser"
              [size]="getProfileSize()"
              [isRegularMatIcon]="true"
              >
            </fi-profile-icon>
          }
        </div>
        `,
    styleUrls: ['./fi-chat-conversation-image.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-chat-conversation-image' },
    standalone: false,
})
export class FiChatConversationImageComponent implements OnInit, OnDestroy {
    @Input() conversation: IChatConversation;
    @Input() profileSize: FiProfileSizes;
    protected conversationUsers: IChatConversationUser[];

    constructor(
        private fiChatHelperService: FiChatHelperService,
        private fiChatSyncService: FiChatSyncService,
        private cdr: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        this.setConversationItemAvatars();

        this.fiChatSyncService
            .conversationUpdated$(this.conversation.Id)
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(() => {
                this.setConversationItemAvatars();
                this.cdr.markForCheck();
            });
    }

    private setConversationItemAvatars() {
        this.conversationUsers = [];
        if (this.conversation.Type === FiChatConversationType.OneToOne) {
            this.conversationUsers.push(this.getChatAvatar());
        } else if (this.conversation.Type === FiChatConversationType.Group) {
            this.conversationUsers = this.getGroupConversationAvatar();
        }
    }

    private getChatAvatar(): IChatConversationUser {
        return this.conversation.Users.find((user) => !this.isCurrentUser(user));
    }

    private getGroupConversationAvatar(): IChatConversationUser[] {
        const notDeletedUsers = this.conversation.Users.filter((user: IChatConversationUser) => !user.IsDeleted);
        if (notDeletedUsers.length === 1) {
            return notDeletedUsers;
        }

        return notDeletedUsers.filter((user: IChatConversationUser) => !this.isCurrentUser(user)).slice(0, 2);
    }

    getProfileSize(): number {
        if (this.profileSize !== undefined) {
            return this.profileSize;
        }
        return this.conversationUsers.length === 1 ? FiProfileSizes.Size32px : FiProfileSizes.Size24px;
    }

    getConversationImageStyleClasses() {
        return {
            // note: commented because Mattias told that we don't need red badge FOR NOW https://chainformation.atlassian.net/browse/FI-4329
            // 'unread-conversation': this.fiNotificationHelperService.isNotVisited(this.conversation),
            wide: this.conversationUsers.length !== 1 || this.profileSize === FiProfileSizes.Size24px,
        };
    }

    private isCurrentUser(conversationUser: IChatConversationUser): boolean {
        return this.fiChatHelperService.isCurrentUser(conversationUser.Id);
    }

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
